import { useEffect, useMemo } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Alert, Button, Container, Stack, Text, Loader, Paper } from '@mantine/core'
import { IconAlertCircle } from '@tabler/icons-react'
import { AdminNavbar } from '@/components/AdminNavbar/AdminNavbar'
import { ResponsiveTitle } from '@/components/ResponsiveTitle/ResponsiveTitle'
import { SeatingChart } from '@/components/SeatingChart/SeatingChart'
import { isAdminAuthenticated, refreshAdminSession } from '@/lib/auth'
import { useGuests } from '@/hooks/useGuests'
import { useSeating } from '@/hooks/useSeating'
import { getAttendingGuests } from '@/lib/seatingUtils'
import styles from './seating.module.css'

export const Route = createFileRoute('/admin/seating')({
  component: AdminSeatingPage,
})

function AdminSeatingPage() {
  const navigate = useNavigate()
  const { guests, loading: guestsLoading, refetch: refetchGuests } = useGuests()
  const {
    configurations,
    activeConfigurationId,
    activeConfigurationName,
    savedState,
    loading: seatingLoading,
    saving,
    promoting,
    configBusy,
    selectConfiguration,
    createConfiguration,
    updateConfigurationName,
    deleteConfiguration,
    saveSeating,
    promoteTestGuest,
    error,
    refetch,
  } = useSeating()

  useEffect(() => {
    if (!isAdminAuthenticated()) {
      navigate({ to: '/admin/login' })
      return
    }

    refreshAdminSession()

    const interval = setInterval(() => {
      if (!isAdminAuthenticated()) {
        navigate({ to: '/admin/login' })
      }
    }, 60000)

    return () => clearInterval(interval)
  }, [navigate])

  const loading = guestsLoading || seatingLoading
  const attendingGuests = useMemo(() => getAttendingGuests(guests), [guests])

  return (
    <div className={styles.adminLayout}>
      <AdminNavbar />

      <Container size="xl" py="md" px="md">
        <Stack gap="sm">
          <ResponsiveTitle order={1}>Sitzplan</ResponsiveTitle>
          <Text size="sm" c="dimmed">
            Gäste mit Zusage Tischen zuweisen. Testnutzer nur in dieser Konfiguration. Mehrere
            Konfigurationen möglich (z.B. Plan A / Plan B).
          </Text>

          {loading ? (
            <Stack align="center" gap="sm" py="md">
              <Loader size="md" />
              <Text size="sm" c="dimmed">
                Lädt Sitzplan...
              </Text>
            </Stack>
          ) : error ? (
            <Alert icon={<IconAlertCircle size={16} />} title="Fehler" color="red" variant="light">
              <Stack gap="sm">
                <Text size="sm">{error}</Text>
                <Button size="xs" variant="light" onClick={() => refetch()}>
                  Erneut versuchen
                </Button>
              </Stack>
            </Alert>
          ) : !savedState ? (
            <Alert icon={<IconAlertCircle size={16} />} title="Kein Sitzplan" color="yellow" variant="light">
              <Text size="sm">Es wurde keine Sitzplan-Konfiguration gefunden.</Text>
            </Alert>
          ) : (
            <Paper p="md" shadow="sm" radius="md" withBorder>
              <SeatingChart
                guests={attendingGuests}
                savedState={savedState}
                saving={saving}
                promoting={promoting}
                configBusy={configBusy}
                configurations={configurations}
                activeConfigurationId={activeConfigurationId}
                activeConfigurationName={activeConfigurationName}
                onSave={saveSeating}
                onPromoteTestGuest={promoteTestGuest}
                onGuestsChanged={refetchGuests}
                onSelectConfiguration={selectConfiguration}
                onCreateConfiguration={createConfiguration}
                onUpdateConfigurationName={updateConfigurationName}
                onDeleteConfiguration={deleteConfiguration}
              />
            </Paper>
          )}
        </Stack>
      </Container>
    </div>
  )
}
